import {
  Alert,
  Button,
  Card,
  Heading,
  Link,
  Paragraph,
  useMediaQuery,
} from "@digdir/designsystemet-react";
import { ChevronRightIcon, ExternalLinkIcon } from "@navikt/aksel-icons";
import { useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { useShouldShowPasswordProtection } from "@/components/PasswordProtection";
import { UthevdTekst } from "@/components/UthevdTekst";
import { env } from "@/env";
import { cn } from "@/utils";
import landingPageImage from "/fjernvarme-illu-web.webp";
import { DokumentasjonsLenker } from "../dokumentasjon/DokumentasjonsLenker";
import { NyttigeLenkerList } from "../shared/NyttigeLenker";
import { VeiledningLenke } from "../shared/VeiledningLenke";
import { GlobaltVarsel } from "../varsler/GlobaltVarsel";

type ErrorCodeProps = {
  errorCode?: string;
};

export function LandingPage({ errorCode }: ErrorCodeProps) {
  const { t } = useTranslation();
  return (
    <div>
      <GlobaltVarsel />
      <main className="container mx-auto grid grid-cols-1 xl:grid-cols-[1fr_auto] py-14 px-10 gap-6 gap-x-12 max-w-page">
        <article className="col-span-1">
          <LandingHeading />
          <LandingImage className="xl:hidden" />
          <LandingInfo errorCode={errorCode} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-9">
            <Card className="h-full space-y-5">
              <Heading level={2} data-size="xs">
                {t("pages.landing.kundeportal.tittel")}
              </Heading>
              <Paragraph data-size="sm">
                {t("pages.landing.kundeportal.beskrivelse")}
              </Paragraph>
              <Link
                href={env.appKundeportalUrl}
                target="_blank"
                rel="noreferrer"
              >
                <div className="flex justify-between">
                  <Paragraph> {t("pages.landing.kundeportal.lenke")}</Paragraph>
                  <ChevronRightIcon fontSize={"2rem"} className="pl-2" />
                </div>
              </Link>
            </Card>

            <VeiledningLenke className="h-fit" />
            <DokumentasjonsLenker className="h-fit" />
            <NyttigeLenkerList className="max-w-full! w-full" />
          </div>
        </article>
        <div className="col-span-1">
          <LandingImage className="hidden xl:block mb-8" />
        </div>
      </main>
    </div>
  );
}

function LandingHeading() {
  const { t } = useTranslation();
  const isLarge = useMediaQuery("(min-width: 1024px)");
  // TODO password: Fjern når passordbeskyttelse ikke er nødvendig lenger. Husk å fjerne komponenten og hooken også.
  const passwordProtection = useShouldShowPasswordProtection();
  return (
    <div className="flex flex-col gap-6 col-span-1">
      {passwordProtection.passwordIsRequired && (
        <div
          role="alert"
          className="flex flex-col gap-4 bg-balder-background-warning border border-balder-border-warning p-8 round rounded-2xl"
          data-color="warning"
        >
          <Heading level={3} data-size="xs">
            {t("pages.landing.ikke-aapnet-enda.tittel")}
          </Heading>
          <Paragraph>
            {t("pages.landing.ikke-aapnet-enda.beskrivelse")}
          </Paragraph>
          <Paragraph>
            <Link
              target="_blank"
              rel="noreferrer"
              href={t("eksterneLenker.merOmNorgespris.url")}
            >
              {t("pages.landing.ikke-aapnet-enda.url-tittel")}
              <ExternalLinkIcon fontSize={"2rem"} className="pl-2" />
            </Link>
          </Paragraph>
        </div>
      )}
      <Heading level={1} data-size={isLarge ? "xl" : "lg"} className="mb-6">
        {t("app.tittel")}
      </Heading>
    </div>
  );
}

function LandingInfo({ errorCode }: ErrorCodeProps) {
  const { t } = useTranslation();
  // TODO password: Fjern når passordbeskyttelse ikke er nødvendig lenger. Husk å fjerne komponenten og hooken også.
  const passwordProtection = useShouldShowPasswordProtection();
  return (
    <div className="flex flex-col gap-6 col-span-2 lg:col-span-1">
      <Paragraph data-size={"xl"}>{t("pages.landing.ingress")}</Paragraph>

      {errorCode && <FeilmeldingForInnlogging errorCode={errorCode} />}
      {!passwordProtection.passwordIsRequired && <LogInButton />}

      <Heading level={2} data-size={"sm"}>
        {t("pages.landing.paa-vegne-av.tittel")}
      </Heading>

      <Paragraph>
        <Trans
          i18nKey="pages.landing.paa-vegne-av.beskrivelse"
          components={{
            Uthevd: <UthevdTekst />,
          }}
        />
      </Paragraph>

      <Link
        href={t("eksterneLenker.altinn-roller.url")}
        target="_blank"
        rel="noreferrer"
      >
        {t("eksterneLenker.altinn-roller.label")}
        <ExternalLinkIcon fontSize={"2rem"} className="pl-2" />
      </Link>
    </div>
  );
}

function FeilmeldingForInnlogging({ errorCode }: ErrorCodeProps) {
  const { t } = useTranslation();
  if (!errorCode) {
    return;
  }
  return (
    <Alert data-color="danger" data-size="sm" className="mt-8">
      <Heading data-size="xs">
        {t("feilmelding.overskrift", { context: errorCode })}
      </Heading>
      <Paragraph>
        {t("feilmelding.beskrivelse", { context: errorCode })}
      </Paragraph>
    </Alert>
  );
}

type ClassNameProps = {
  className?: string;
};
function LandingImage({ className }: ClassNameProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "max-w-xl col-span-2 lg:col-span-1 mx-auto lg:mx-0",
        className,
      )}
    >
      <img
        src={landingPageImage}
        alt={t("pages.landing.hjelpetekst-bilde")}
        width="100%"
        className="w-full object-scale-down"
      />
    </div>
  );
}

function LogInButton({ className }: ClassNameProps) {
  const { t } = useTranslation();
  const navgate = useNavigate();

  return (
    <div
      className={cn(
        "col-span-2 flex justify-center lg:justify-start mt-6 mb-6",
        className,
      )}
    >
      <Button
        className="w-fit lg:w-max"
        onClick={() => navgate({ to: "/auth/logg-inn" })}
      >
        {t("pages.landing.button.logg-inn")}
        <ChevronRightIcon fontSize="1.5rem" className="shrink-0" />
      </Button>
    </div>
  );
}
